import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/Chart";
import { QUERY_KEY } from "@/constant/queryKey";
import { fetchDashboardData } from "@/services/api-service";
import {
  ADMIN_DASHBOARD_ENDPOINT,
  AdminDashboardBarData,
} from "@/services/core/admin-dashboard-charts.api";
import { useDashboardStore } from "@/store/dashboard-store";
import { useMerchantStore } from "@/store/merchant-store";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis } from "recharts";
import { OverViewChartSkeleton } from "./OverViewChartSkeleton";

const chartConfig = {
  volume: {
    label: "Transaction Volume",
    color: "hsl(var(--chart-1))",
  },
  revenue: {
    label: "Revenue",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const OverViewChart = () => {
  const [activeMetric, setActiveMetric] = useState<"volume" | "revenue">(
    "volume",
  );
  const { selectedMerchant } = useMerchantStore();
  const { dateRange } = useDashboardStore();

  const endPoint = `${ADMIN_DASHBOARD_ENDPOINT}/${selectedMerchant?.name}/bar-data`;
  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.ADMIN_DASHBOARD_OVERVIEW,
      selectedMerchant?.name,
      dateRange,
    ],
    queryFn: () =>
      fetchDashboardData<AdminDashboardBarData>(endPoint, {
        username: selectedMerchant?.name ?? "",
        from: dateRange.from,
        to: dateRange.to,
      }),
  });

  const barChartData = useMemo(() => {
    if (!data?.payload) return [];

    return data.payload.map((el) => ({ date: el.date, ...el.channels }));
  }, [data]);

  const channelNames = useMemo(() => {
    if (!barChartData.length) return [];

    const firstDataPoint = barChartData[0];
    return Object.keys(firstDataPoint).filter((key) => key !== "date");
  }, [barChartData]);

  const total = useMemo(() => {
    if (!barChartData.length) return { volume: 0, revenue: 0 };

    return {
      volume: barChartData.reduce((acc, curr) => {
        const channelSum = channelNames.reduce(
          (sum, channel) =>
            sum +
            ((curr[channel as keyof typeof curr] as { volume?: number })
              ?.volume || 0),
          0,
        );
        return acc + channelSum;
      }, 0),

      revenue: barChartData.reduce((acc, curr) => {
        const channelSum = channelNames.reduce(
          (sum, channel) =>
            sum +
            ((curr[channel as keyof typeof curr] as { revenue?: number })
              ?.revenue || 0),
          0,
        );
        return acc + channelSum;
      }, 0),
    };
  }, [barChartData, channelNames]);

  if (isLoading) {
    return <OverViewChartSkeleton />;
  }

  if (!data?.payload?.length) {
    return (
      <Card>
        <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
          <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
            <CardTitle>Payment Methods Analysis</CardTitle>
            <CardDescription>
              Daily transaction metrics by payment method
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex h-[400px] items-center justify-center px-10">
          <div className="text-center">
            <p className="text-muted-foreground text-sm">
              No data available for the selected date range
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>Payment Methods Analysis</CardTitle>
          <CardDescription>
            Daily transaction metrics by payment method
          </CardDescription>
        </div>
        <div className="flex">
          {["volume", "revenue"].map((key) => {
            const metric = key as keyof typeof chartConfig;
            return (
              <button
                key={metric}
                data-active={activeMetric === metric}
                className="data-[active=true]:bg-muted/50 relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l sm:border-t-0 sm:border-l sm:px-8 sm:py-6"
                onClick={() => setActiveMetric(metric)}
              >
                <span className="text-muted-foreground text-xs">
                  {chartConfig[metric].label}
                </span>
                <span className="text-lg leading-none font-bold sm:text-3xl">
                  {metric === "revenue"
                    ? `$${total[metric].toLocaleString()}`
                    : total[metric].toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <BarChart data={barChartData} margin={{ left: 12, right: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) =>
                activeMetric === "revenue"
                  ? `$${value / 1000}k`
                  : value.toLocaleString()
              }
            />
            <ChartTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-background rounded-lg border p-2 shadow-sm">
                      <div className="grid gap-2">
                        <div className="flex flex-col">
                          <span className="text-muted-foreground text-[0.70rem] uppercase">
                            Date
                          </span>
                          <span className="font-bold">
                            {new Date(
                              payload[0].payload.date,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        {payload.map((entry) => (
                          <div
                            key={entry.name}
                            className="flex justify-between gap-2"
                          >
                            <span className="text-muted-foreground text-[0.70rem] uppercase">
                              {entry.name}
                            </span>
                            <span className="font-bold">
                              {activeMetric === "revenue"
                                ? `$${entry.value?.toLocaleString() ?? 0}`
                                : entry.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            {/* <Bar
              dataKey={`creditCard.${activeMetric}`}
              name="Credit Card"
              fill="#60a5fa"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey={`paypal.${activeMetric}`}
              name="PayPal"
              fill="#2563eb"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey={`bankTransfer.${activeMetric}`}
              name="Bank Transfer"
              fill="#1d4ed8"
              radius={[4, 4, 0, 0]}
            /> */}
            {channelNames.map((channel, idx) => (
              <Bar
                key={channel + idx}
                dataKey={`${channel}.${activeMetric}`}
                name={channel.replace(/([A-Z])/g, " $1").trim()}
                // fill={chartConfig[channel as keyof typeof chartConfig].color}
                fill={`hsl(${210 + idx * 20} 84% 67%)`}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default OverViewChart;
